'use client';

import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { File as FileIcon, X } from 'lucide-react';
import * as React from 'react';

import type { FileItemContextValue, FileUploadFile } from './file-upload';
import { FileItemContext, useFileItemContext, useFileUploadContext } from './file-upload';

type FileUploadFileListProps = React.ComponentProps<'ul'>;

const FileUploadFileList = ({ className, children, ...props }: FileUploadFileListProps) => {
  return (
    <ul data-slot="file-upload-file-list" className={cn('grid gap-2', className)} {...props}>
      {children}
    </ul>
  );
};

const fileItemVariants = cva(
  'group relative grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card transition-colors ' +
    'data-error:border-destructive/50',
  {
    variants: {
      variant: {
        default: 'px-3 py-2.5',
        compact: 'px-2 py-1.5',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

type FileUploadFileItemProps = React.ComponentProps<'li'> &
  VariantProps<typeof fileItemVariants> & {
    file: FileUploadFile;
  };

const FileUploadFileItem = ({ file, variant = 'default', className, children, ...props }: FileUploadFileItemProps) => {
  const ctxValue = React.useMemo<FileItemContextValue>(() => ({ file }), [file]);

  return (
    <FileItemContext.Provider value={ctxValue}>
      <li
        data-slot="file-upload-file-item"
        data-error={file.error ? '' : undefined}
        className={cn(fileItemVariants({ variant, className }))}
        {...props}
      >
        {children}
      </li>
    </FileItemContext.Provider>
  );
};

type FileUploadFileItemPreviewProps = React.ComponentProps<'div'>;

const FileUploadFileItemPreview = ({ className, ...props }: FileUploadFileItemPreviewProps) => {
  const { file } = useFileItemContext();

  return (
    <div
      data-slot="file-upload-file-item-preview"
      className={cn('flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted', className)}
      {...props}
    >
      {file.previewUrl ? (
        <img src={file.previewUrl} alt={file.file.name} className="size-full object-cover" />
      ) : (
        <FileIcon className="size-5 text-muted-foreground" />
      )}
    </div>
  );
};

type FileUploadFileItemNameProps = React.ComponentProps<'div'>;

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
};

const FileUploadFileItemName = ({ className, ...props }: FileUploadFileItemNameProps) => {
  const { file } = useFileItemContext();

  return (
    <div data-slot="file-upload-file-item-name" className={cn('min-w-0 flex-1', className)} {...props}>
      <p className="truncate font-medium text-sm">{file.file.name}</p>
      <p className="text-muted-foreground text-xs">{formatFileSize(file.file.size)}</p>
      {file.error && (
        <p className="text-destructive text-xs" role="alert">
          {file.error}
        </p>
      )}
    </div>
  );
};

type FileUploadFileItemProgressProps = React.ComponentProps<'div'>;

const FileUploadFileItemProgress = ({ className, ...props }: FileUploadFileItemProgressProps) => {
  const { file } = useFileItemContext();

  if (file.progress === undefined) return null;

  return (
    <div
      data-slot="file-upload-file-item-progress"
      role="progressbar"
      aria-valuenow={file.progress}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Uploading ${file.file.name}`}
      className={cn('col-span-2 h-1.5 overflow-hidden rounded-full bg-muted', className)}
      {...props}
    >
      <div
        className="h-full rounded-full bg-primary transition-[width] duration-200"
        style={{ width: `${Math.min(100, Math.max(0, file.progress))}%` }}
      />
    </div>
  );
};

type FileUploadFileItemDeleteTriggerProps = React.ComponentProps<'button'>;

const FileUploadFileItemDeleteTrigger = ({
  className,
  type = 'button',
  children,
  ...props
}: FileUploadFileItemDeleteTriggerProps) => {
  const { files, onFilesChange, disabled } = useFileUploadContext();
  const { file } = useFileItemContext();

  const handleClick = React.useCallback(() => {
    onFilesChange(files.filter((f) => f.id !== file.id));
  }, [files, onFilesChange, file.id]);

  return (
    <button
      data-slot="file-upload-file-item-delete-trigger"
      type={type}
      disabled={disabled}
      onClick={handleClick}
      aria-label={`Remove ${file.file.name}`}
      className={cn(
        'ml-auto shrink-0 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity',
        'hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100',
        'disabled:pointer-events-none',
        className,
      )}
      {...props}
    >
      {children ?? <X className="size-4" />}
    </button>
  );
};

export {
  FileUploadFileItem,
  FileUploadFileItemDeleteTrigger,
  FileUploadFileItemName,
  FileUploadFileItemPreview,
  FileUploadFileItemProgress,
  FileUploadFileList,
  fileItemVariants,
  formatFileSize,
};
